import BoldSmallText from '@/components/BoldSmallText';
import BoldText from '@/components/BoldText';
import { Collapsible } from '@/components/Collapsible';
import { CheckIcon } from '@/components/Icons';
import RegularText from '@/components/RegularText';
import useUserPlan from '@/hooks/useUserPlan';
import { ColumnContentType, Icon, IndexTable, Text } from '@shopify/polaris';
import { XIcon } from '@shopify/polaris-icons';
import { IndexTableHeading } from '@shopify/polaris/build/ts/src/components/IndexTable';
import { NonEmptyArray } from '@shopify/polaris/build/ts/src/types';
import React, { useMemo } from 'react';
import { ComparePlansStyled } from './styled';

interface IData {
  groupName: string;
  content: {
    title: string;
    description?: string;
    value: { free: string | boolean; standard: string | boolean; pro: string | boolean; plus: string | boolean };
  }[];
}
const ComparePlans = () => {
  const { isActivePlusPlan } = useUserPlan();

  const columnConfig = useMemo(() => {
    const columnContentTypes: ColumnContentType[] = ['text', 'text', 'text', 'text'];
    const headings: NonEmptyArray<IndexTableHeading> = [
      { title: '', id: 'groupName' },
      { title: <BoldText>Free</BoldText>, id: 'free' },
      { title: <BoldText>Standard</BoldText>, id: 'standard' },
      { title: <BoldText>Pro</BoldText>, id: 'pro' },
      isActivePlusPlan ? { title: <BoldText>Plus</BoldText>, id: 'plus' } : undefined,
    ].filter((item) => !!item) as NonEmptyArray<IndexTableHeading>;
    return { columnContentTypes, headings };
  }, [isActivePlusPlan]);

  const rows = useMemo(() => {
    const data: IData[] = [
      {
        groupName: '',
        content: [
          { title: 'ETA views', value: { free: 'Unlimited', standard: 'Unlimited', pro: 'Unlimited', plus: 'Unlimited' } },
        ],
      },
      {
        groupName: '',
        content: [
          { title: 'Synced products', value: { free: 'Unlimited', standard: 'Unlimited', pro: 'Unlimited', plus: 'Unlimited' } },
        ],
      },
      {
        groupName: 'Estimate rule setup',
        content: [
          {
            title: 'Number of rules',
            value: { free: '1', standard: '3', pro: 'Unlimited', plus: 'Unlimited' },
          },
          {
            title: 'Item conditions',
            description: 'Products, variants, collections, vendors',
            value: { free: false, standard: true, pro: true, plus: true },
          },
          {
            title: 'Location conditions',
            description: 'Regions, countries, states, zipcodes',
            value: { free: false, standard: false, pro: true, plus: true },
          },
          {
            title: 'Import CSV zipcode',
            value: { free: false, standard: false, pro: true, plus: true },
          },
          {
            title: 'Show EDD in Cart',
            value: { free: false, standard: true, pro: true, plus: true },
          },
          {
            title: 'Show EDD when out of stock',
            value: { free: false, standard: true, pro: true, plus: true },
          },
        ],
      },
      {
        groupName: 'Appearance',
        content: [
          { title: 'Visual editor', value: { free: false, standard: true, pro: true, plus: true } },
          {
            title: 'Timeline icon',
            value: { free: false, standard: true, pro: true, plus: true },
          },
        ],
      },
      {
        groupName: 'Translation',
        content: [{ title: 'Additional languages', value: { free: false, standard: '3', pro: 'Unlimited', plus: 'Unlimited' } }],
      },
      {
        groupName: 'Geolocation and validity',
        content: [
          { title: 'Auto-detect zipcode/country', value: { free: false, standard: false, pro: true, plus: true } },
          { title: 'Require valid zipcode to add to cart', value: { free: false, standard: false, pro: true, plus: true } },
        ],
      },
      {
        groupName: 'Display and position',
        content: [
          { title: 'Product page', value: { free: true, standard: true, pro: true, plus: true } },
          {
            title: 'Collection, Home, Featured page',
            description: 'Compatibility may vary by themes',
            value: { free: true, standard: true, pro: true, plus: true },
          },
          {
            title: 'Cart Page/Drawer/Popup',
            value: { free: false, standard: true, pro: true, plus: true },
          },
          {
            title: 'Email',
            value: { free: false, standard: true, pro: true, plus: true },
          },
          // {
          //   title: 'Checkout, Thank you, Order status page',
          //   description: 'Show static EDD',
          //   value: { free: false, standard: true, pro: true, plus: true },
          // },
        ],
      },
      isActivePlusPlan
        ? {
            groupName: 'Powered by UI Extensions',
            content: [
              {
                title: 'ETA Checkout Extensions',
                description: 'Show dynamic EDD for shipping methods in Checkout',
                value: { free: false, standard: false, pro: false, plus: true },
              },
            ],
          }
        : undefined,
      {
        groupName: 'Analytics',
        content: [
          {
            title: 'ETA banners analytics',
            value: { free: false, standard: 'Last 7-day data', pro: 'Full analytics', plus: 'Full analytics' },
          },
        ],
      },
      {
        groupName: 'Services',
        content: [
          {
            title: 'Chat support',
            value: { free: '24/7', standard: '24/7', pro: 'High priority 24/7', plus: 'VIP priority 24/7' },
          },
          {
            title: 'Customization',
            description: 'Professional customization by developers and consultancy by experts',
            value: { free: 'Limited', standard: 'Medium priority', pro: 'High priority', plus: 'VIP priority 24/7' },
          },
          {
            title: 'Feature request',
            value: { free: 'Low priority', standard: 'Medium priority', pro: 'High priority', plus: 'VIP priority' },
          },
        ],
      },
    ].filter((item) => !!item);

    const handleRenderValue = (value: string | boolean) => {
      if (typeof value === 'boolean') {
        return value ? <CheckIcon fill="#29845A" /> : <Icon source={XIcon} tone="critical" />;
      }
      return value;
    };

    return data.map((item, index) => (
      <React.Fragment key={index}>
        {item.groupName && (
          <IndexTable.Row id={item.groupName} position={index}>
            <IndexTable.Cell className="bg-gray" colSpan={5}>
              <BoldSmallText>{item.groupName}</BoldSmallText>
            </IndexTable.Cell>
          </IndexTable.Row>
        )}
        {item.content.map((content, contentIndex) => (
          <IndexTable.Row key={contentIndex} id={content.title} position={contentIndex}>
            <IndexTable.Cell>
              <RegularText>{content.title}</RegularText>
              {content.description && (
                <Text as="p" variant="bodySm">
                  {content.description}
                </Text>
              )}
            </IndexTable.Cell>
            <IndexTable.Cell>
              <RegularText>{handleRenderValue(content.value.free)}</RegularText>
            </IndexTable.Cell>
            <IndexTable.Cell>
              <RegularText>{handleRenderValue(content.value.standard)}</RegularText>
            </IndexTable.Cell>
            <IndexTable.Cell>
              <RegularText>{handleRenderValue(content.value.pro)}</RegularText>
            </IndexTable.Cell>
            {isActivePlusPlan && (
              <IndexTable.Cell>
                <RegularText>{handleRenderValue(content.value.plus)}</RegularText>
              </IndexTable.Cell>
            )}
          </IndexTable.Row>
        ))}
      </React.Fragment>
    ));
  }, [isActivePlusPlan]);

  return (
    <ComparePlansStyled>
      <Collapsible title="Compare plans" defaultOpen={false}>
        <div className="mt-16">
          <IndexTable headings={columnConfig.headings} selectable={false} itemCount={rows.length}>
            {rows}
          </IndexTable>
        </div>
      </Collapsible>
    </ComparePlansStyled>
  );
};

export default ComparePlans;
